import { useEffect, useState } from "react";
import { useLogout } from "../../../hooks/useLogout";
import DropdownDepartmentsInput from "../../InputComponents/DropdownDepartmentsInput/DropdownDepartmentsInput";
import RadioButtonInput from "../../InputComponents/RadioButtonInput/RadioButtonInput";
import TextInput from "../../InputComponents/TextInput/TextInput";

// my components
import { useAuthContext } from "../../../hooks/useAuthContext";
import Question from "../../InputComponents/Question/Question";
import SpecialInputForm from "../../InputComponents/SpecialInputForm/SpecialInputForm";

// modal components
import ModalApplicationSubmitted from "../../InputComponents/ModalApplicationSubmitted/ModalApplicationSubmitted";
import ModalMessage from "../../InputComponents/ModalMessage/ModalMessage";

// styles
import "../../../index.css";
import Style from "./FirstInputForm.module.css";

// utils
import translateMissingFields from "../../../utils/translateMissingFields";

// data
import { firstFormQuestions as questions } from "../../../constants/formQuestions";

// context
import { FirstInputFormDataContext } from "../../../context/FirstInputFormDataContext";
import NumberInput from "../../InputComponents/NumberInput/NumberInput";
import ProjectSummary from "../../InputComponents/ProjectSummary/ProjectSummary";

// API requests
import { useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import getAuthHeader from "../../../utils/getAuthHeader";
import CalendarInputAdvanced from "../../InputComponents/CalendarInputAdvanced/CalendarInputAdvanced";
import NewEmploymentPositions from "../../InputComponents/NewEmploymentPositions/NewEmploymentPositions";
import NumberInputSelect from "../../InputComponents/NumberInputSelect/NumberInputSelect";
import { getDepartments } from "./firstInputFormApi";

const FirstInputForm = () => {
  const { logout } = useLogout();
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [nameSurname, setNameSurname] = useState("");
  const [vocation, setVocation] = useState("");
  const [departmentId, setDepartment] = useState("");
  const [email, setEmail] = useState("");
  const [projectTitle, setProjectTitle] = useState("");
  const [projectTitleEnglish, setProjectTitleEnglish] = useState("");
  const [projectAcronym, setProjectAcronym] = useState("");
  const [applicationDeadline, setApplicationDeadline] = useState("");
  const [projectSummary, setProjectSummary] = useState("");
  const [applicationURL, setApplicationURL] = useState("");
  const [projectApplicant, setProjectAplicant] = useState("");
  const [projectPartners, setProjectPartners] = useState("");
  const [totalValue, setTotalValue] = useState(null);
  const [fesbValuePart, setFesbValuePart] = useState(null);
  const [newEmploymentBoolean, setNewEmployment] = useState(null);
  const [newEmploymentPositions, setNewEmploymentPositions] = useState([]);
  const [projectTeam, setProjectTeam] = useState([]);
  const [firstInputMarker] = useState(true);
  const [secondInputMarker] = useState(false);

  // MISSING FIELDS MODAL
  const [modalMessageIsOpen, setModalMessageIsOpen] = useState(false);
  const [missingFields, setMissingFields] = useState([]);

  // modal application submitted
  const [modalApplicationSubmittedIsOpen, setModalApplicationSubmittedIsOpen] =
    useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const cookies = new Cookies(null, { path: "/" });

  const handleSubmit = async () => {
    setIsSubmitted(true);
    const userId = cookies.get("userId");

    const inputFormData = {
      userId: userId,
      firstInputMarker,
      secondInputMarker,
      nameSurname,
      vocation,
      departmentId,
      email,
      projectTitle,
      projectTitleEnglish,
      projectAcronym,
      applicationDeadline,
      projectSummary,
      applicationURL,
      projectApplicant,
      projectPartners,
      totalValue,
      fesbValuePart,
      newEmploymentBoolean,
      newEmploymentPositions,
      projectTeam,
    };

    try {
      const response = await fetch("/api/projectInfo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
        body: JSON.stringify(inputFormData),
      });

      if (response.ok) {
        await response.json();

        sessionStorage.clear();
        setModalApplicationSubmittedIsOpen(true);
      } else {
        const errorData = await response.json().catch(() => null);

        const errorMessage = errorData
          ? errorData.error
          : `Error: ${response.status} ${response.statusText}`;

        if (
          errorData &&
          errorData.emptyFields &&
          errorData.emptyFields.length > 0
        ) {
          setMissingFields(errorData.emptyFields);
          setModalMessageIsOpen(true);
          throw new Error(errorMessage);
        }
      }
    } catch (error) {
      console.error("Error posting data:", error.message);
      window.alert("Greška u slanju podataka: ", error.message);
    }
  };

  const handleClick = () => {
    logout();
    navigate("/");
  };

  const handleGetDepartments = async () => {
    try {
      const departments = await getDepartments();
      const fetchedDepartments = departments.map((department) => {
        return {
          fullName: department.name + " - " + department.headName,
          id: department.id,
        };
      });

      setDepartmentsData(fetchedDepartments);
    } catch (error) {
      console.error("Error fetching department data:", error);
      window.alert("Greška u dohvaćanju podataka o odjelima!");
    }
  };

  useEffect(() => {
    handleGetDepartments();
  }, []);

  const [departmentsData, setDepartmentsData] = useState([]);

  return (
    <FirstInputFormDataContext.Provider
      value={{
        projectTeam,
        setProjectTeam,
        totalValue,
        setModalMessageIsOpen,
        setModalApplicationSubmittedIsOpen,
        missingFields,
        newEmploymentPositions,
        setNewEmploymentPositions,
      }}
    >
      {modalMessageIsOpen && (
        <ModalMessage
          modalMessage={translateMissingFields(missingFields)}
          height={"auto"}
          setModalIsOpen={setModalMessageIsOpen}
          isMissingFieldsModal={true}
        />
      )}
      {modalApplicationSubmittedIsOpen && <ModalApplicationSubmitted />}

      <div className={Style.InputContainer}>
        {user && (
          <div className={Style.Logout}>
            <p>{user?.username.split("@")[0]}</p>
            <button onClick={handleClick}>Odjavi se</button>
            {(user.isAdmin || user.isModerator) && (
              <button onClick={() => navigate("/AdminDashboard")}>
                Dashboard
              </button>
            )}
          </div>
        )}
        <div className={Style.InputForm}>
          <h1 className="document-title">NAMJERA PRIJAVE</h1>
          <Question questionText={questions[0]} />
          <TextInput
            label={"IME I PREZIME"}
            name="ime_prezime"
            setSpecificState={setNameSurname}
            initialValue={user?.fullName}
            isError={isSubmitted && !nameSurname}
            isInFirstInput={true}
            isDisabled={true}
          />
          <TextInput
            label={"TITULA**"}
            name={"titula"}
            setSpecificState={setVocation}
            isError={isSubmitted && !vocation}
          />
          <DropdownDepartmentsInput
            label={"ZAVOD (ODSJEK)**"}
            name={"zavod"}
            data={departmentsData}
            setSpecificState={setDepartment}
            isDepartment={true}
            isError={isSubmitted && !departmentId}
          />
          <TextInput
            label={"E-MAIL"}
            name={"email"}
            setSpecificState={setEmail}
            type={"email"}
            initialValue={user?.username || ""}
            isError={isSubmitted && !email}
            isInFirstInput={true}
            isDisabled={true}
          />
          <Question questionText={questions[1]} />
          <TextInput
            label={"NAZIV PROJEKTA**"}
            name={"naziv_projekta"}
            setSpecificState={setProjectTitle}
            isError={isSubmitted && !projectTitle}
          />
          <TextInput
            label={"NAZIV PROJEKTA NA ENGLESKOM JEZIKU**"}
            name={"naziv_projekta_na_engleskom_jeziku"}
            setSpecificState={setProjectTitleEnglish}
            isError={isSubmitted && !projectTitleEnglish}
          />
          <TextInput
            label={"AKRONIM PROJEKTA**"}
            name={"akronim_projekta"}
            setSpecificState={setProjectAcronym}
            isError={isSubmitted && !projectAcronym}
          />
          <CalendarInputAdvanced
            label={"ROK ZA PRIJAVU PROJEKTA**"}
            name={"rok_za_prijavu_projekta"}
            setSpecificState={setApplicationDeadline}
            workingDaysLimit={7}
            isAdminDashboard={false}
            isError={isSubmitted && !applicationDeadline}
          />

          <Question questionText={questions[2]} />
          <ProjectSummary
            name={"sazetak"}
            setSpecificState={setProjectSummary}
            isError={isSubmitted && !projectSummary}
          />

          <Question questionText={questions[3]} />
          <TextInput
            label={
              "LINK NA STRANICU NA KOJOJ SE NALAZI POTPUNA DOKUMENTACIJA**"
            }
            name={"link_na_stranicu_s_dokumentacijom"}
            setSpecificState={setApplicationURL}
            isError={isSubmitted && !applicationURL}
          />

          <Question questionText={questions[4]} />
          <TextInput
            label={
              "PRIJAVITELJ PROJEKTA/VODEĆI PARTNER (INSTITUCIJA, TVRTKA...)**"
            }
            name={"prijavitelj_projekta"}
            setSpecificState={setProjectAplicant}
            isError={isSubmitted && !projectApplicant}
          />

          <Question questionText={questions[5]} />
          <TextInput
            hasTitle={false}
            name={"ostali_partneri_na_projektu"}
            setSpecificState={setProjectPartners}
            isError={isSubmitted && !projectPartners}
          />

          <Question questionText={questions[6]} />
          <NumberInput
            label={
              "UKUPNA VRIJEDNOST(ukoliko trenutno nije poznat točan iznos, navesti okviran iznos)**"
            }
            name={"ukupna_vrijednost_projekta"}
            setSpecificState={setTotalValue}
            currencySign={"EUR"}
            isError={isSubmitted && totalValue === null}
          />
          <NumberInputSelect
            label={
              "DIO PRORAČUNA KOJI PRIPADA FESB-u(vrijednost ili postotak ukupne vrijednosti)**"
            }
            name={"dio_proracuna_fesb"}
            setSpecificState={setFesbValuePart}
            isFirstInputForm={true}
            isSecondInputForm={false}
            isError={isSubmitted && fesbValuePart === null}
          />
          {fesbValuePart > totalValue && (
            <p className={Style.WarningMessage}>
              Iznos FESB proračuna ne smije biti veći od ukupne vrijednosti
              projekta
            </p>
          )}

          <Question questionText={questions[7]} />
          <RadioButtonInput
            simpleQuestionValue={""}
            setSelectionState={setNewEmployment}
            isError={isSubmitted && newEmploymentBoolean === null}
          />
          {newEmploymentBoolean && <NewEmploymentPositions formType="first" />}

          <SpecialInputForm
            name="project_team_members"
            question={questions[8]}
            formType={"first"}
          />

          <button className="default-button" onClick={handleSubmit}>
            PODNESI OBRAZAC NAMJERE
          </button>
        </div>
      </div>
    </FirstInputFormDataContext.Provider>
  );
};

export default FirstInputForm;
